import { clsx } from 'clsx';
import { eachDayOfInterval, format, parseISO } from 'date-fns';
import { useMemo, useRef } from 'react';

import type { AssignmentShiftList } from '../../../../../../js/api';
import { ScheduleTypeEnum } from '../../../../../../js/api';
import { Badge } from '../../../../components/badge';
import { BaseCalendar, type BookedRange } from '../../../../components/base-calendar';
import { Button } from '../../../../components/button';
import type { StatusCodes } from '../../../../constants/workflows';
import { useIsMdUp } from '../../../../hooks/useIsMdUp';
import { parseApiLocalDate, parseFormDate } from '../../../../utils/date';
import { getShiftStatusBucket } from '../../../../utils/globalStyles';

interface Props {
  currentMonth: Date;
  onMonthChange: (date: Date) => void;
  /** Earliest date the user can navigate to (matches the data window). */
  windowStart: Date;
  /** Latest date the user can navigate to (matches the data window). */
  windowEnd: Date;
  /** Provider's current schedule_type. Drives paint mode + tap intent. */
  scheduleType: ScheduleTypeEnum;
  /** Server-known editing dates (allows under CLOSED/STRICT, blocks under OPEN). */
  serverDates: string[];
  /** Local optimistic adds not yet flushed to the server. */
  pendingAdds: Set<string>;
  /** Local optimistic removes not yet flushed to the server. */
  pendingRemoves: Set<string>;
  /** Shifts overlapping the visible window (drives booked-day painting). */
  shifts: AssignmentShiftList[];
  /** Called when the user taps a date that is not part of a booked shift. */
  onToggleDate: (dateISO: string) => void;
  /** Called when the user taps a date that is part of a booked shift. */
  onAssignmentClick: (assignmentId: string) => void;
  /** Days marked available in the currently visible months. */
  availableDaysInView: number;
  /** Days with at least one confirmed shift in the currently visible months. */
  confirmedDaysCount: number;
}

const DATE_FORMAT = 'yyyy-MM-dd';

/**
 * Presentational calendar grid for the Provider schedule page.
 *
 * Wraps the shared BaseCalendar with mode-aware paint:
 *
 *  - CLOSED / STRICT: `serverDates` are the provider's explicit allow rows;
 *    they render as solid-green "selected" cells. Everything else stays
 *    unmarked. Tap toggles an allow row.
 *
 *  - OPEN: `serverDates` are the provider's explicit BLOCK rows. Every
 *    non-booked, non-blocked day in the window is fed to `availableDates`
 *    so it renders green-available; the blocks are passed as
 *    `selectedDates` and styled red via the `data-schedule-mode="OPEN"`
 *    wrapper override in tailwind.css. Tap a green cell to add a block;
 *    tap a red cell to remove it.
 *
 * The selection diff path is identical in all three modes — only the
 * data inputs and visual treatment change. The bulk mutation upstream
 * routes the diff to the right `is_blocked` bucket per the provider's
 * current schedule_type.
 */
export const ScheduleCalendarGrid = ({
  currentMonth,
  onMonthChange,
  windowStart,
  windowEnd,
  scheduleType,
  serverDates,
  pendingAdds,
  pendingRemoves,
  shifts,
  onToggleDate,
  onAssignmentClick,
  availableDaysInView,
  confirmedDaysCount,
}: Props) => {
  const isMdUp = useIsMdUp();
  const isOpenMode = scheduleType === ScheduleTypeEnum.OPEN;

  // displayedDates = (serverDates ∪ pendingAdds) − pendingRemoves
  // Under CLOSED/STRICT these are allow rows; under OPEN these are blocks.
  const displayedDates = useMemo<string[]>(() => {
    const next = new Set<string>(serverDates);
    pendingAdds.forEach((iso) => next.add(iso));
    pendingRemoves.forEach((iso) => next.delete(iso));
    return Array.from(next);
  }, [serverDates, pendingAdds, pendingRemoves]);

  // Map shifts → BookedRange[]. One range per shift, placed on the shift's
  // start_on date only. We deliberately ignore end_on (and time fields):
  // a night shift starting Mon 7pm and ending Tue 7am paints Monday only,
  // because Tuesday morning's bleed doesn't preclude another shift starting
  // Tuesday evening. Shifts whose status doesn't bucket (terminal-negative;
  // the server already filters these, defensive) or that are missing
  // start_on are skipped.
  const bookedRanges = useMemo<BookedRange[]>(() => {
    const ranges: BookedRange[] = [];
    shifts.forEach((shift) => {
      const bucket = getShiftStatusBucket(shift.current_status.code as StatusCodes);
      if (!bucket) return;
      const start = parseApiLocalDate(shift.start_on);
      if (!start) return;
      ranges.push({
        from: start,
        to: start,
        bucket,
        assignmentId: shift.assignment,
      });
    });
    return ranges;
  }, [shifts]);

  // Collect booked-day ISOs so we can strip them out of availableDates /
  // selectedDates below. Booked dates shouldn't render as "available" or
  // "selected" — the booked modifier class should own the cell's visual
  // treatment, otherwise the .rdp-day_selected / .rdp-day_available rules
  // (which appear later in tailwind.css) paint over the booked color.
  const bookedDateSet = useMemo<Set<string>>(() => {
    const set = new Set<string>();
    bookedRanges.forEach((range) => set.add(format(range.from, DATE_FORMAT)));
    return set;
  }, [bookedRanges]);

  // Under OPEN, paint every non-booked, non-blocked day in the window as
  // available (green). Under CLOSED/STRICT, the available set is just the
  // displayed allow rows.
  const visibleAvailableDates = useMemo<string[]>(() => {
    if (!isOpenMode) {
      return displayedDates.filter((iso) => !bookedDateSet.has(iso));
    }
    const blockSet = new Set(displayedDates);
    return eachDayOfInterval({ start: windowStart, end: windowEnd })
      .map((d) => format(d, DATE_FORMAT))
      .filter((iso) => !bookedDateSet.has(iso) && !blockSet.has(iso));
  }, [displayedDates, bookedDateSet, isOpenMode, windowStart, windowEnd]);

  // selectedDates = the user's marked dates. Under OPEN those are the blocks
  // (which look red via the data-schedule-mode wrapper CSS override). Under
  // CLOSED/STRICT they're the allow rows (default green-selected styling).
  const selectedIsoSource = useMemo<string[]>(
    () => displayedDates.filter((iso) => !bookedDateSet.has(iso)),
    [displayedDates, bookedDateSet]
  );

  // BaseCalendar expects Date[] for `selected`. parseISO matches the same
  // round-trip BaseCalendar uses internally (format(date, 'yyyy-MM-dd')), and
  // for bare YYYY-MM-DD strings it produces a local-midnight Date — no TZ shift.
  //
  // Date instances are cached by ISO so the same date keeps the same Date
  // reference across renders. react-day-picker keys its per-day rendering
  // off the items in the selected array; if every render produced brand-new
  // Date instances (the default of `.map(parseISO)`), RDP would re-mount
  // every selected cell on every parent render — which manifests as a
  // visible repaint / bounce on the cell that just changed AND on every
  // other selected cell.
  const dateCacheRef = useRef<Map<string, Date>>(new Map());
  const selectedDates = useMemo<Date[]>(
    () =>
      selectedIsoSource.map((iso) => {
        const cached = dateCacheRef.current.get(iso);
        if (cached) return cached;
        const next = parseISO(iso);
        dateCacheRef.current.set(iso, next);
        return next;
      }),
    [selectedIsoSource]
  );

  // BaseCalendar's onSelectedDatesChange hands back the full next selection
  // array. Diff against the current selected set (NOT availableDates under
  // OPEN — under OPEN the available set is a much larger window-minus-blocks
  // pool). The user can only click one non-booked cell at a time, so the
  // diff is always one date.
  const handleSelectedDatesChange = (next: Date[]) => {
    const nextIsoSet = new Set(
      next.map((d) => parseFormDate(d)).filter((iso): iso is string => iso !== null)
    );
    const currentIsoSet = new Set(selectedIsoSource);
    nextIsoSet.forEach((iso) => {
      if (!currentIsoSet.has(iso)) onToggleDate(iso);
    });
    currentIsoSet.forEach((iso) => {
      if (!nextIsoSet.has(iso)) onToggleDate(iso);
    });
  };

  const handleToday = () => onMonthChange(new Date());

  return (
    // On md+ the calendar (and its Today/stats row) sits inside its own
    // bordered/white card to match the Add Availability box above and the
    // other home sections; on mobile the calendar already fills the
    // viewport via `fullWidth`, so it stays unframed to avoid a
    // redundant box.
    //
    // `data-schedule-mode` is a CSS hook for the OPEN-mode selection
    // override in tailwind.css. Under OPEN the wrapper repaints
    // `.rdp-day_selected` (which holds the provider's blocks) as red
    // instead of the default green-teal; CLOSED/STRICT keep the default.
    <div
      data-schedule-mode={scheduleType}
      className={clsx(
        'tw:mx-auto tw:max-w-3xl tw:shadow-lg',
        'tw:md:rounded-lg tw:md:border tw:md:border-brand-green-1/50 tw:md:bg-white tw:md:p-3'
      )}
    >
      <div className="tw:mb-2 tw:flex tw:justify-start tw:gap-2 tw:flex-wrap">
        <Button
          type="button"
          aria-label="Jump to today"
          onClick={handleToday}
          color="brand-green-outline"
          small
        >
          Today
        </Button>
        <Badge color={isOpenMode ? 'rose' : 'blue'}>
          <span className="tw:font-semibold">{availableDaysInView}</span>
          <span>{isOpenMode ? 'days blocked' : 'days available'}</span>
        </Badge>
        <Badge color="emerald">
          <span className="tw:font-semibold">{confirmedDaysCount}</span>
          <span>days confirmed</span>
        </Badge>
      </div>
      <BaseCalendar
        availableDates={visibleAvailableDates}
        selectedDates={selectedDates}
        onSelectedDatesChange={handleSelectedDatesChange}
        currentMonth={currentMonth}
        onMonthChange={onMonthChange}
        numberOfMonths={isMdUp ? 2 : 1}
        fromDate={windowStart}
        toDate={windowEnd}
        bookedRanges={bookedRanges}
        onBookedDateClick={onAssignmentClick}
        fullWidth={!isMdUp}
        clickableAllDates
        pastReadOnly
      />
    </div>
  );
};
